import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { CertificationService, CertificationCreateRequest } from '../../../core/services/certification.service';
import { OrganizationService } from '../../../core/services/organization.service';
import { UnifiedOnboardingService } from '../../../core/services/unified-onboarding.service';
import { User } from '../../../core/models/user.model';
import { Organization } from '../../../core/models/organization.model';
import {
  Certification,
  CertificationType,
  CertificationOwnerType,
  CertificationTypeInfo,
  ORGANIZATION_CERTIFICATION_TYPES,
  USER_CERTIFICATION_TYPES
} from '../../../core/models/certification.model';
import { toCdnUrl } from '../../../shared/utils/format.utils';
import { toDateInputValue } from '../../../shared/utils/date.utils';
import { MaxLengthDirective } from '../../../shared/directives/max-length.directive';
import { CharCounterDirective } from '../../../shared/directives/char-counter.directive';
import { FORM_MAX_LENGTH } from '../../../shared/constants/form-limits';
import { FileUploadService } from '../../../core/services/file-upload.service';

/**
 * Certification Form Component
 * Handles adding and editing certifications for both organizations and users
 */
@Component({
  selector: 'app-certification-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaxLengthDirective, CharCounterDirective],
  templateUrl: './certification-form.component.html',
  styleUrls: ['./certification-form.component.scss'],
  host: { class: 'certification-form' }
})
export class CertificationFormComponent implements OnInit {
  certForm!: FormGroup;
  readonly maxNotesLength = FORM_MAX_LENGTH.LARGE;
  isEditMode = false;
  isRenewalMode = false;
  isLoading = false;
  isUploading = false;
  currentUser: User | null = null;
  currentOrganization: Organization | null = null;
  existingCertification: Certification | null = null;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  // File upload state
  selectedFile: File | null = null;
  uploadedDocumentUrl: string | null = null;
  uploadedFileName: string | null = null;

  // Route params
  certificationId: string | null = null;
  certificationTypeParam: CertificationType | null = null;
  ownerTypeParam: CertificationOwnerType | null = null;
  previousCertificationId: string | null = null;

  // Available certification types based on owner type
  availableCertTypes: CertificationTypeInfo[] = [];

  // US States for state-specific licenses
  usStates = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
  ];

  private unifiedOnboardingService = inject(UnifiedOnboardingService);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private certificationService: CertificationService,
    private fileUploadService: FileUploadService,
    private organizationService: OrganizationService
  ) {}

  ngOnInit(): void {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });

    // Load current organization
    this.loadCurrentOrganization();

    // Get route params
    this.route.paramMap.subscribe(params => {
      this.certificationId = params.get('id');
      this.isEditMode = !!this.certificationId;
    });

    this.route.queryParamMap.subscribe(params => {
      this.certificationTypeParam = params.get('type') as CertificationType;
      this.ownerTypeParam = params.get('ownerType') as CertificationOwnerType;
      this.isRenewalMode = params.get('renew') === 'true';
      this.previousCertificationId = params.get('previousId');

      // Set available cert types based on owner type
      if (this.ownerTypeParam === 'organization') {
        this.availableCertTypes = ORGANIZATION_CERTIFICATION_TYPES;
      } else if (this.ownerTypeParam === 'profile') {
        this.availableCertTypes = USER_CERTIFICATION_TYPES;
      } else {
        // Show all if not specified
        this.availableCertTypes = [...ORGANIZATION_CERTIFICATION_TYPES, ...USER_CERTIFICATION_TYPES];
      }
    });

    this.initializeForm();

    if (this.isEditMode && this.certificationId) {
      this.loadExistingCertification();
    } else if (this.isRenewalMode && this.previousCertificationId) {
      this.loadPreviousCertificationForRenewal();
    } else if (this.certificationTypeParam) {
      // Pre-select certification type if passed via query param
      this.certForm.patchValue({
        certificationType: this.certificationTypeParam,
        ownerType: this.ownerTypeParam || 'organization'
      });
    }
  }

  async loadCurrentOrganization(): Promise<void> {
    try {
      this.currentOrganization = await this.organizationService.getMyOrganization();
    } catch (error) {
      console.error('Error loading organization:', error);
    }
  }

  initializeForm(): void {
    this.certForm = this.fb.group({
      // Certification type
      certificationType: ['', Validators.required],
      ownerType: [this.ownerTypeParam || 'organization', Validators.required],

      // Certificate details
      certificateNumber: ['', Validators.required],
      issuedDate: ['', Validators.required],
      expirationDate: [''],
      issuingAuthority: ['Federal Aviation Administration', Validators.required],

      // State (for state-specific licenses)
      state: [''],

      // Document upload
      documentFile: [null],

      // Notes
      notes: ['', [Validators.maxLength(FORM_MAX_LENGTH.LARGE)]]
    });

    // Update issuing authority based on certification type
    this.certForm.get('certificationType')?.valueChanges.subscribe(type => {
      this.updateFormForCertType(type);
    });
  }

  updateFormForCertType(certType: CertificationType): void {
    const stateControl = this.certForm.get('state');
    const authorityControl = this.certForm.get('issuingAuthority');

    if (certType === 'state-applicator') {
      stateControl?.setValidators([Validators.required]);
      authorityControl?.setValue('State Department of Agriculture');
    } else if (certType === 'insurance') {
      stateControl?.clearValidators();
      stateControl?.setValue('');
      authorityControl?.setValue('Insurance Provider');
    } else {
      stateControl?.clearValidators();
      stateControl?.setValue('');
      authorityControl?.setValue('Federal Aviation Administration');
    }

    stateControl?.updateValueAndValidity();
  }

  async loadExistingCertification(): Promise<void> {
    if (!this.certificationId) return;

    this.isLoading = true;

    try {
      const certification = await this.certificationService.getCertification(this.certificationId);

      if (certification) {
        this.existingCertification = certification;
        this.populateForm(certification);

        // Set available cert types based on existing certification
        if (certification.ownerType === 'organization') {
          this.availableCertTypes = ORGANIZATION_CERTIFICATION_TYPES;
        } else {
          this.availableCertTypes = USER_CERTIFICATION_TYPES;
        }

        // Set existing document info
        if (certification.documentUrl) {
          this.uploadedDocumentUrl = certification.documentUrl;
          this.uploadedFileName = certification.documentFileName || 'Existing document';
        }
      }
    } catch (error) {
      console.error('Error loading certification:', error);
      this.errorMessage = 'Failed to load certification';
    } finally {
      this.isLoading = false;
    }
  }

  async loadPreviousCertificationForRenewal(): Promise<void> {
    if (!this.previousCertificationId) return;

    this.isLoading = true;

    try {
      const certification = await this.certificationService.getCertification(this.previousCertificationId);

      if (certification) {
        this.existingCertification = certification;

        // Pre-fill form with previous certification data but clear dates
        this.certForm.patchValue({
          certificationType: certification.certificationType,
          ownerType: certification.ownerType,
          certificateNumber: '', // Clear for new cert number
          issuedDate: '', // Clear for new date
          expirationDate: '',
          issuingAuthority: certification.issuingAuthority,
          state: certification.state || '',
          notes: ''
        });

        // Set available cert types
        if (certification.ownerType === 'organization') {
          this.availableCertTypes = ORGANIZATION_CERTIFICATION_TYPES;
        } else {
          this.availableCertTypes = USER_CERTIFICATION_TYPES;
        }
      }
    } catch (error) {
      console.error('Error loading previous certification:', error);
      this.errorMessage = 'Failed to load previous certification for renewal';
    } finally {
      this.isLoading = false;
    }
  }

  populateForm(cert: Certification): void {
    this.certForm.patchValue({
      certificationType: cert.certificationType,
      ownerType: cert.ownerType,
      certificateNumber: cert.certificateNumber,
      issuedDate: toDateInputValue(cert.issuedDate),
      expirationDate: toDateInputValue(cert.expirationDate),
      issuingAuthority: cert.issuingAuthority,
      state: cert.state || '',
      notes: cert.notes || ''
    });

    // The notes column allowed 4000 chars before this cap existed, so older
    // records can exceed it. onSubmit() bails on an invalid form, which would
    // leave the user unable to save any edit until they trimmed notes they may
    // not have written. Drop the length rule for those legacy values — the
    // maxlength attribute still stops them growing any further.
    if ((cert.notes?.length ?? 0) > FORM_MAX_LENGTH.LARGE) {
      const notesControl = this.certForm.get('notes');
      notesControl?.clearValidators();
      notesControl?.updateValueAndValidity({ emitEvent: false });
    }
  }

  async onSubmit(): Promise<void> {
    if (this.certForm.invalid) {
      this.certForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;

    try {
      // Upload document if selected
      if (this.selectedFile && !this.uploadedDocumentUrl) {
        await this.uploadDocument();
      }

      const formValue = this.certForm.value;

      // Build certification data
      const certificationData: CertificationCreateRequest = {
        certificationType: formValue.certificationType,
        ownerType: formValue.ownerType,
        certificateNumber: formValue.certificateNumber,
        issuedDate: formValue.issuedDate,
        expirationDate: formValue.expirationDate || undefined,
        issuingAuthority: formValue.issuingAuthority,
        state: formValue.state || undefined,
        notes: formValue.notes || undefined,
        documentUrl: this.uploadedDocumentUrl || undefined,
        documentFileName: this.uploadedFileName || undefined
      };

      // Add organization ID for org certifications
      if (formValue.ownerType === 'organization' && this.currentOrganization) {
        certificationData.organizationId = this.currentOrganization.id;
      }

      if (this.isEditMode && this.existingCertification) {
        // Update existing certification
        await this.certificationService.updateCertification(this.existingCertification.id, {
          certificateNumber: certificationData.certificateNumber,
          issuedDate: certificationData.issuedDate,
          expirationDate: certificationData.expirationDate,
          issuingAuthority: certificationData.issuingAuthority,
          state: certificationData.state,
          notes: certificationData.notes,
          documentUrl: certificationData.documentUrl,
          documentFileName: certificationData.documentFileName
        });
        this.successMessage = 'Certification updated successfully';
      } else if (this.isRenewalMode && this.previousCertificationId) {
        // Create renewal (archive old, create new)
        await this.certificationService.renewCertification(this.previousCertificationId, certificationData);
        this.successMessage = 'Certification renewed successfully';
      } else {
        // Create new certification
        await this.certificationService.createCertification(certificationData);
        this.successMessage = 'Certification added successfully';
      }

      // Refresh the onboarding checklist (fire-and-forget). Org certs need a
      // server-side resync first; profile certs are already handled on write.
      if (formValue.ownerType === 'organization') {
        this.unifiedOnboardingService.syncAndRefresh();
      } else {
        this.unifiedOnboardingService.loadProgress();
      }

      // Navigate back to certifications list after short delay
      setTimeout(() => {
        this.router.navigate(['/certifications']);
      }, 1000);
    } catch (error: any) {
      console.error('Error saving certification:', error);
      this.errorMessage = this.toSaveErrorMessage(error, this.certForm.value.ownerType);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Better error handling when file uploading errors
   */
  private toSaveErrorMessage(error: any, ownerType: string): string {
    if (error?.status === 403 && ownerType === 'organization') {
      const action = this.isRenewalMode ? 'renew' : this.isEditMode ? 'edit' : 'add';
      return `You do not have permission to ${action} certifications for this `
        + 'organization. Only an Owner or Chief Supervisor can.';
    }
    return error?.error?.error
      || error?.message
      || 'Failed to save certification. Please try again.';
  }
  
  async uploadDocument(): Promise<void> {
    if (!this.selectedFile) return;

    this.isUploading = true;

    try {
      const result = await this.certificationService.uploadCertificationDocument(this.selectedFile);
      this.uploadedDocumentUrl = result.url;
      this.uploadedFileName = result.fileName;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw new Error('Failed to upload document');
    } finally {
      this.isUploading = false;
    }
  }

  async viewDocument(): Promise<void> {
    if (this.existingCertification?.id && this.existingCertification?.documentUrl) {
      try {
        const signedUrl = await this.certificationService.getDownloadUrl(this.existingCertification.id);
        window.open(toCdnUrl(signedUrl), '_blank');
      } catch (error) {
        console.error('Failed to get download URL:', error);
      }
    }
  }

  onCancel(): void {
    this.router.navigate(['/certifications']);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.errorMessage = 'Please upload a PDF, JPEG, or PNG file';
        this.selectedFile = null;
        this.certForm.patchValue({ documentFile: null });
        return;
      }

      // Validate file size
      const maxSize = this.fileUploadService.getMaxFileSize();
      if (file.size > maxSize) {
        this.errorMessage = 'File size must be less than ' + this.fileUploadService.formatFileSize(maxSize);
        this.selectedFile = null;
        this.certForm.patchValue({ documentFile: null });
        return;
      }

      this.selectedFile = file;
      this.certForm.patchValue({ documentFile: file });
      this.errorMessage = null;

      // Clear previously uploaded document URL since we have a new file
      this.uploadedDocumentUrl = null;
      this.uploadedFileName = null;
    }
  }

  get pageTitle(): string {
    if (this.isRenewalMode) {
      return 'Renew Certification';
    }
    return this.isEditMode ? 'Edit Certification' : 'Add Certification';
  }

  get submitButtonText(): string {
    if (this.isLoading) {
      return 'Saving...';
    }
    if (this.isRenewalMode) {
      return 'Renew Certification';
    }
    return this.isEditMode ? 'Update Certification' : 'Add Certification';
  }

  get selectedCertType(): CertificationTypeInfo | undefined {
    const type = this.certForm.get('certificationType')?.value;
    return this.availableCertTypes.find(t => t.value === type);
  }

  get isStateRequired(): boolean {
    return this.certForm.get('certificationType')?.value === 'state-applicator';
  }

  get previousCertNumber(): string | null {
    if (this.isRenewalMode && this.existingCertification) {
      return this.existingCertification.certificateNumber;
    }
    return null;
  }

  get hasDocument(): boolean {
    return !!this.selectedFile || !!this.uploadedDocumentUrl;
  }

  get documentDisplayName(): string {
    if (this.selectedFile) {
      return this.selectedFile.name;
    }
    if (this.uploadedFileName) {
      return this.uploadedFileName;
    }
    return '';
  }
}
